import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSeedling, faLightbulb, faStar, faCheck } from '@fortawesome/free-solid-svg-icons';
import { Modal } from './Modal';
import './HelpModal.css';

export const YouthAcademyHelpModal: React.FC<{ isOpen: boolean; onClose: () => void; }> = ({ isOpen, onClose }) => (
    <Modal isOpen={isOpen} onClose={onClose} className="help-modal">
        <h2><FontAwesomeIcon icon={faSeedling} style={{ marginRight: '8px', color: 'var(--positive-color)' }} />Youth Academy</h2>
        <p style={{ fontSize: '1.1em', marginBottom: '24px' }}>Develop young talent and create a pipeline of future stars for your team.</p>

        <div className="help-section">
            <h3><FontAwesomeIcon icon={faStar} style={{ marginRight: '8px', color: 'var(--warning-color)' }} />Academy Levels & Benefits</h3>

            <div style={{ marginTop: '12px', padding: '12px', backgroundColor: 'var(--primary-color)', borderRadius: '4px', marginBottom: '12px' }}>
                <strong>Level 1: Basic Scouting Network</strong>
                <div style={{ fontSize: '0.9em', marginTop: '4px' }}>
                    <div>Weekly Cost: £2,000</div>
                    <div style={{ marginTop: '4px' }}>Minimal talent scouting. Low-quality youth players.</div>
                </div>
            </div>

            <div style={{ marginTop: '12px', padding: '12px', backgroundColor: 'var(--primary-color)', borderRadius: '4px', marginBottom: '12px' }}>
                <strong>Level 2: Regional Academy</strong>
                <div style={{ fontSize: '0.9em', marginTop: '4px' }}>
                    <div>Upgrade Cost: £1,000,000 | Weekly Cost: £15,000</div>
                    <div style={{ marginTop: '4px', color: 'var(--positive-color)' }}>
                        <FontAwesomeIcon icon={faCheck} style={{ marginRight: '4px' }} />Better scouting network
                    </div>
                </div>
            </div>

            <div style={{ marginTop: '12px', padding: '12px', backgroundColor: 'var(--primary-color)', borderRadius: '4px', marginBottom: '12px' }}>
                <strong>Level 3: National Academy</strong>
                <div style={{ fontSize: '0.9em', marginTop: '4px' }}>
                    <div>Upgrade Cost: £3,000,000 | Weekly Cost: £50,000</div>
                    <div style={{ marginTop: '4px', color: 'var(--positive-color)' }}>
                        <FontAwesomeIcon icon={faCheck} style={{ marginRight: '4px' }} />Quality young prospects
                    </div>
                    <div style={{ color: 'var(--positive-color)' }}>
                        <FontAwesomeIcon icon={faCheck} style={{ marginRight: '4px' }} />Better development potential
                    </div>
                </div>
            </div>

            <div style={{ marginTop: '12px', padding: '12px', backgroundColor: 'var(--primary-color)', borderRadius: '4px', marginBottom: '12px' }}>
                <strong>Level 4: Continental Talent Hub</strong>
                <div style={{ fontSize: '0.9em', marginTop: '4px' }}>
                    <div>Upgrade Cost: £8,000,000 | Weekly Cost: £150,000</div>
                    <div style={{ marginTop: '4px', color: 'var(--positive-color)' }}>
                        <FontAwesomeIcon icon={faCheck} style={{ marginRight: '4px' }} />International recruitment
                    </div>
                    <div style={{ color: 'var(--positive-color)' }}>
                        <FontAwesomeIcon icon={faCheck} style={{ marginRight: '4px' }} />High-potential stars emerge
                    </div>
                </div>
            </div>

            <div style={{ marginTop: '12px', padding: '12px', backgroundColor: 'var(--primary-color)', borderRadius: '4px', marginBottom: '12px' }}>
                <strong>Level 5: Global Youth Programme</strong>
                <div style={{ fontSize: '0.9em', marginTop: '4px' }}>
                    <div>Upgrade Cost: £20,000,000 | Weekly Cost: £400,000</div>
                    <div style={{ marginTop: '4px', color: 'var(--positive-color)' }}>
                        <FontAwesomeIcon icon={faCheck} style={{ marginRight: '4px' }} />Elite young talent discovery
                    </div>
                    <div style={{ color: 'var(--positive-color)' }}>
                        <FontAwesomeIcon icon={faCheck} style={{ marginRight: '4px' }} />Future superstars in pipeline
                    </div>
                </div>
            </div>

            <div style={{ marginTop: '12px', padding: '12px', backgroundColor: 'var(--primary-color)', borderRadius: '4px' }}>
                <strong>Level 6: World Class Development Centre</strong>
                <div style={{ fontSize: '0.9em', marginTop: '4px' }}>
                    <div>Weekly Cost: £800,000</div>
                    <div style={{ marginTop: '4px', color: 'var(--positive-color)' }}>
                        <FontAwesomeIcon icon={faCheck} style={{ marginRight: '4px' }} />World-class young prospects
                    </div>
                    <div style={{ color: 'var(--positive-color)' }}>
                        <FontAwesomeIcon icon={faCheck} style={{ marginRight: '4px' }} />Develop next generation of stars
                    </div>
                </div>
            </div>
        </div>

        <div className="help-section">
            <h3><FontAwesomeIcon icon={faLightbulb} style={{ marginRight: '8px', color: 'var(--warning-color)' }} />Recruitment Preferences</h3>
            <p style={{ marginBottom: '12px' }}>Guide your academy to develop players matching your tactical vision. Set preferences to influence which young talent gets generated.</p>

            <div style={{ backgroundColor: 'rgba(0, 255, 155, 0.1)', padding: '12px', borderRadius: '4px', marginBottom: '12px' }}>
                <strong style={{ display: 'block', marginBottom: '8px' }}>Available Preferences:</strong>
                <ul style={{ margin: '0', paddingLeft: '20px', lineHeight: '1.8', fontSize: '0.95em' }}>
                    <li><strong>Role Focus:</strong> Influence position distribution (Balanced, Attacking, Defensive, Goalkeeper)</li>
                    <li><strong>Foot Preference:</strong> Bias left-footed or right-footed players in intake</li>
                    <li><strong>Width Focus:</strong> Generate more central or wide players</li>
                    <li><strong>Playing Style:</strong> Develop technical, pace-focused, or physical attributes in youth players</li>
                </ul>
            </div>

            <div style={{ backgroundColor: 'rgba(255, 200, 0, 0.1)', padding: '12px', borderRadius: '4px', fontSize: '0.95em', lineHeight: '1.6' }}>
                <strong>⏰ Effectiveness Over Time:</strong><br/>
                Preferences are most effective when set 4 weeks before youth intake. As intake date approaches, effectiveness diminishes:
                <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                    <li>4 weeks before intake: 100% effectiveness</li>
                    <li>3 weeks before: 75% effectiveness</li>
                    <li>2 weeks before: 50% effectiveness</li>
                    <li>1 week before: 25% effectiveness</li>
                </ul>
            </div>
        </div>

        <div className="help-section">
            <h3><FontAwesomeIcon icon={faLightbulb} style={{ marginRight: '8px', color: 'var(--warning-color)' }} />Youth Development Strategy</h3>
            <div style={{ backgroundColor: 'rgba(0, 255, 155, 0.1)', padding: '12px', borderRadius: '4px' }}>
                <ul style={{ margin: '0', paddingLeft: '20px', lineHeight: '1.8' }}>
                    <li><strong>Long-term Value:</strong> Young players develop into stars if given time and training.</li>
                    <li><strong>Squad Building:</strong> Promote youth players gradually to save transfer costs.</li>
                    <li><strong>Sell High:</strong> Develop young talent to high skill levels then sell for profit.</li>
                    <li><strong>Budget Control:</strong> Downgrade academy during financial struggles - reduced player quality but saves weekly wages.</li>
                    <li><strong>Succession Planning:</strong> Keep upgrading to maintain a steady stream of prospects as players age.</li>
                </ul>
            </div>
        </div>

        <div className="modal-actions">
            <button className="button primary" onClick={onClose}>Got it!</button>
        </div>
    </Modal>
);
